"use client";

import { useCallback } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { type RecaptchaAction } from "@/lib/recaptcha-shared";

export const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim() ?? "";
export const RECAPTCHA_ENABLED = RECAPTCHA_SITE_KEY.length > 0;

export type RecaptchaTokenResult = { ok: true; token: string } | { ok: false; error: string };

export function useRecaptchaToken() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getToken = useCallback(
    async (action: RecaptchaAction): Promise<RecaptchaTokenResult> => {
      if (!RECAPTCHA_ENABLED) {
        return {
          ok: false,
          error: "reCAPTCHA is not configured. Add NEXT_PUBLIC_RECAPTCHA_SITE_KEY.",
        };
      }

      if (!executeRecaptcha) {
        return {
          ok: false,
          error: "reCAPTCHA is still loading. Please try again.",
        };
      }

      try {
        const token = await executeRecaptcha(action);
        if (!token) {
          return { ok: false, error: "Failed to generate reCAPTCHA token." };
        }
        return { ok: true, token };
      } catch {
        return { ok: false, error: "Failed to verify reCAPTCHA. Please try again." };
      }
    },
    [executeRecaptcha],
  );

  return { getToken };
}
